import React, { useState, useMemo, memo, useCallback } from 'react';
import OpenHouseCard from '../components/OpenHouseCard';
import DayFilterDropdown from '../components/DayFilterDropdown';
import { StyleConstants } from '../StyleConstants';
import { useOpenHouses } from '../services/openHouseDataService';
import { generateDayFilterOptions, filterOpenHousesByDay } from '../utils/dayFilterUtils';
import '../GalleryMobile.css'; // Styles for mobile gallery view

// Modern card grid layout styles
const styles = {
  container: {
    width: '100%', 
    maxWidth: '100%',
    margin: 0,
    padding: 0, // No padding here since it's handled by the parent container
    backgroundColor: 'transparent', // Changed to transparent to let parent background show through
    boxSizing: 'border-box',
  },
  gridContainer: {
    marginTop: '8px',
    marginBottom: '32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px', /* Matches SplitView's spacing */
    marginTop: 0,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  sectionTitle: {
    ...StyleConstants.sectionTitle,
    margin: 0,
    lineHeight: '1.15', /* Exactly matching SplitView's lineHeight */
    padding: 0
  },
  statusCounter: StyleConstants.statusCounter,
  noListings: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: 'var(--grey)',
    color: '#666',
    borderRadius: '8px',
    fontSize: '16px',
  }
};

function OpenHouseGalleryView({ isMobile }) {
  const { openHouses, loading, error } = useOpenHouses();
  const [selectedDay, setSelectedDay] = useState('all');

  // Memoize the day change handler to prevent unnecessary re-renders
  const handleDayChange = useCallback((e) => {
    setSelectedDay(e.target.value);
  }, []);

  // Generate day filter options based on available open houses
  const dayFilterOptions = useMemo(() => {
    return generateDayFilterOptions(openHouses);
  }, [openHouses]);

  // Filter and sort open houses by selected day and date/time
  const filteredAndSortedOpenHouses = useMemo(() => {
    if (!openHouses || !Array.isArray(openHouses)) return [];
    
    // First filter by selected day
    const filtered = filterOpenHousesByDay(openHouses, selectedDay);
    
    // Then sort by date/time (earliest first), then by end time as secondary sort
    return [...filtered].sort((a, b) => {
      const startTimeDiff = new Date(a.openHouseStartTime) - new Date(b.openHouseStartTime);
      if (startTimeDiff !== 0) {
        return startTimeDiff; // Earliest first
      }
      // If start times are equal, sort by end time (earliest first)
      return new Date(a.openHouseEndTime) - new Date(b.openHouseEndTime);
    });
  }, [openHouses, selectedDay]);

  // Render the open houses in a grid with optimized loading
  const renderOpenHouses = () => {
    if (loading) {
      return (
        <div style={{ ...styles.noListings, color: '#333' }}>
          Loading open houses...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ ...styles.noListings, color: '#d32f2f' }}>
          Error loading open houses: {error.message}
        </div>
      );
    }

    if (!filteredAndSortedOpenHouses || filteredAndSortedOpenHouses.length === 0) {
      return (
        <div style={styles.noListings}>
          {selectedDay === 'all' ? 'No open houses scheduled' : 'No open houses scheduled for this day'}
        </div>
      );
    }

    return (
      <div 
        className="gallery-view-grid" 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', // From StyleConstants
          width: '100%', // From StyleConstants
          gap: '32px', // From ListingList.jsx (override)
          marginTop: '8px', // From ListingList.jsx
          marginBottom: '32px' // From ListingList.jsx
          // NO justifyItems, NO padding, NO boxSizing as per ListingList.jsx effective style
        }}
      >
        {filteredAndSortedOpenHouses.map((openHouse, index) => (
          <OpenHouseCard 
            key={openHouse.id} 
            openHouse={openHouse} 
            isMobile={isMobile}
            priority={index < 8} // Prioritize first 8 cards for better perceived performance
            loadingDelay={index * 50} // Stagger loading for smoother experience
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '0' }}>
      <div style={styles.container}>
        {/* Header with title, count, and day filter */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{
              ...StyleConstants.sectionTitle,
              margin: 0,
              marginTop: 0,
              marginBottom: 0,
              padding: 0,
              lineHeight: '32px', /* Use exact px value from StyleConstants rather than relative value */
              fontSize: '24px', /* Explicitly set font size to match SplitView */
              fontWeight: '700' /* Explicitly set font weight to match SplitView */
            }}>
              Upcoming
              <span style={StyleConstants.statusCounter}>
                ({filteredAndSortedOpenHouses.length})
              </span>
            </h2>
          </div>
          
          {/* Day Filter Dropdown */}
          <DayFilterDropdown
            value={selectedDay}
            options={dayFilterOptions}
            onChange={handleDayChange}
            viewContext="Gallery"
          />
        </div>
        
        {/* Show open houses in a single grid */}
        {renderOpenHouses()}
      </div>
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(OpenHouseGalleryView);
